<script setup lang="ts">
  import type { Editor } from "@tiptap/vue-3";
  import type { HTMLAttributes } from "vue";
  import { Button } from "@/components/ui/button";
  import { Separator } from "@/components/ui/separator";
  import { Tooltip } from "@/components/ui/tooltip";
  import { cn } from "@/lib/utils";
  import { computed } from "vue";
  import { useTiptapContext } from ".";
  import Icon from "./TiptapIcon.vue";

  const props = withDefaults(
    defineProps<{
      editor?: Editor | null;
      class?: HTMLAttributes["class"];
      variant?: "full" | "mini";
    }>(),
    {
      editor: undefined,
      class: undefined,
      variant: "full",
    },
  );

  // const emits = defineEmits<{}>()

  // Get editor from context if not provided directly
  const { editor: contextEditor } = useTiptapContext();
  const editor = computed(() => props.editor ?? contextEditor.value);

  // Check if editor is ready
  const isEditorReady = computed(() => {
    return editor.value && editor.value.isEditable;
  });

  // Check if the current selection has an active mark
  function isActive(type: string, attributes = {}) {
    if (!isEditorReady.value) return false;

    return editor.value!.isActive(type, attributes);
  }

  // Variant-based visibility
  const isMini = computed(() => props.variant === "mini");
  const showHeadings = computed(() => !isMini.value);
</script>

<template>
  <div
    :class="cn('tiptap-toolbar flex flex-nowrap gap-1 items-center', props.class)"
    data-slot="tiptap-toolbar">
    <!-- History controls -->
    <div class="flex items-center gap-1">
      <Tooltip content="Undo (Ctrl+Z)">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :disabled="!editor?.can().undo()"
          @click="editor?.chain().focus().undo().run()">
          <Icon
            name="mdi:undo"
            class="h-4 w-4" />
        </Button>
      </Tooltip>

      <Tooltip content="Redo (Ctrl+Y)">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :disabled="!editor?.can().redo()"
          @click="editor?.chain().focus().redo().run()">
          <Icon
            name="mdi:redo"
            class="h-4 w-4" />
        </Button>
      </Tooltip>
    </div>

    <Separator
      orientation="vertical"
      class="h-6" />

    <!-- Basic Text Formatting -->
    <div class="flex items-center gap-1">
      <Tooltip content="Bold">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :class="{ 'bg-accent': isActive('bold') }"
          :disabled="!isEditorReady"
          @click="editor?.chain().focus().toggleBold().run()">
          <Icon
            name="mdi:format-bold"
            class="h-4 w-4" />
        </Button>
      </Tooltip>

      <Tooltip content="Italic">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :class="{ 'bg-accent': isActive('italic') }"
          :disabled="!isEditorReady"
          @click="editor?.chain().focus().toggleItalic().run()">
          <Icon
            name="mdi:format-italic"
            class="h-4 w-4" />
        </Button>
      </Tooltip>
    </div>

    <!-- Headings -->
    <template v-if="showHeadings">
      <Separator
        orientation="vertical"
        class="h-6" />

      <div class="flex items-center gap-1">
        <Tooltip content="Heading 1">
          <Button
            size="icon"
            variant="ghost"
            class="h-6 w-6"
            :class="{ 'bg-accent': isActive('heading', { level: 1 }) }"
            :disabled="!isEditorReady"
            @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()">
            <Icon
              name="mdi:format-header-1"
              class="h-4 w-4" />
          </Button>
        </Tooltip>

        <Tooltip content="Heading 2">
          <Button
            size="icon"
            variant="ghost"
            class="h-6 w-6"
            :class="{ 'bg-accent': isActive('heading', { level: 2 }) }"
            :disabled="!isEditorReady"
            @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()">
            <Icon
              name="mdi:format-header-2"
              class="h-4 w-4" />
          </Button>
        </Tooltip>

        <Tooltip content="Heading 3">
          <Button
            size="icon"
            variant="ghost"
            class="h-6 w-6"
            :class="{ 'bg-accent': isActive('heading', { level: 3 }) }"
            :disabled="!isEditorReady"
            @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()">
            <Icon
              name="mdi:format-header-3"
              class="h-4 w-4" />
          </Button>
        </Tooltip>

        <Tooltip content="Paragraph">
          <Button
            size="icon"
            variant="ghost"
            class="h-6 w-6"
            :class="{ 'bg-accent': isActive('paragraph') }"
            :disabled="!isEditorReady"
            @click="editor?.chain().focus().setParagraph().run()">
            <Icon
              name="mdi:format-paragraph"
              class="h-4 w-4" />
          </Button>
        </Tooltip>
      </div>
    </template>

    <Separator
      orientation="vertical"
      class="h-6" />

    <!-- Lists -->
    <div class="flex items-center gap-1">
      <Tooltip content="Bullet List">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :class="{ 'bg-accent': isActive('bulletList') }"
          :disabled="!isEditorReady"
          @click="editor?.chain().focus().toggleBulletList().run()">
          <Icon
            name="mdi:format-list-bulleted"
            class="h-4 w-4" />
        </Button>
      </Tooltip>

      <Tooltip content="Numbered List">
        <Button
          size="icon"
          variant="ghost"
          class="h-6 w-6"
          :class="{ 'bg-accent': isActive('orderedList') }"
          :disabled="!isEditorReady"
          @click="editor?.chain().focus().toggleOrderedList().run()">
          <Icon
            name="mdi:format-list-numbered"
            class="h-4 w-4" />
        </Button>
      </Tooltip>
    </div>
  </div>
</template>
